import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-2-line',
    title: 'Book Free Inspection',
    description:
      'Schedule your free, no-obligation property inspection in under 2 minutes. We respond within 24 hours and send a confirmation with your appointment details.',
  },
  {
    number: '02',
    icon: 'ri-search-eye-line',
    title: 'We Inspect & Document',
    description:
      'Our licensed public adjusters conduct a thorough inspection of all damage. We photograph, measure, and document every single affected area that insurance companies miss.',
  },
  {
    number: '03',
    icon: 'ri-file-paper-2-line',
    title: 'We Handle Your Claim',
    description:
      "We prepare your claim, negotiate directly with your insurance company, and fight for the maximum settlement you're entitled to. You don't deal with the insurer at all.",
  },
  {
    number: '04',
    icon: 'ri-money-dollar-circle-line',
    title: 'You Get Paid',
    description:
      'Once we reach a fair settlement, you receive your payment. We only get paid when you get paid — our fee is a percentage of what we recover for you. No recovery, no fee.',
  },
];

export default function HowItWorks() {
  const { ref, isVisible } = useScrollAnimation();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="how-it-works" className="bg-white py-24 px-6" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-[#EBF4FD] border border-[#93C5F5] rounded-full px-4 py-1.5 mb-4">
            <span className="text-[#1560B8] text-sm font-semibold">SIMPLE PROCESS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0F1927] mb-4">
            How It Works
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            From free inspection to maximum payout — we handle everything. Here's what to expect when you work with Strive Claims.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-[#93C5F5] z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className={`relative bg-white border-2 border-gray-100 rounded-2xl p-8 text-center transition-all duration-700 hover:border-[#5BA8E5] group ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="relative z-10">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#1B78D4] rounded-full mx-auto mb-5 text-white font-extrabold text-xl">
                    {step.number}
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-[#1B78D4] text-3xl group-hover:scale-110 transition-transform duration-200`} />
                  </div>
                  <h3 className="text-[#0F1927] font-extrabold text-lg mb-3">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`mt-14 text-center transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-gray-500 mb-5 text-base">
            Ready to find out how much your claim is really worth?
          </p>
          <button
            onClick={() => scrollTo('booking')}
            className="bg-[#1B78D4] hover:bg-[#1560B8] text-white font-bold px-10 py-4 rounded-lg transition-all duration-200 inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-rocket-line" />
            Start Your Free Inspection Today
          </button>
        </div>
      </div>
    </section>
  );
}
